import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ListChecks, Plus } from "lucide-react";
import { ErrorBanner } from "../components/AuthShell.js";
import { CreateWithAiModal } from "../components/CreateWithAiModal.js";
import { NewChecklistModal } from "../components/NewChecklistModal.js";
import { Button } from "../components/ui/Button.js";
import { CARD_GRID_CLS, CardLink } from "../components/ui/Card.js";
import { EmptyState } from "../components/ui/EmptyState.js";
import { KindChip } from "../components/ui/KindChip.js";
import { PageHeader } from "../components/ui/PageHeader.js";
import { ProgressBar } from "../components/ui/ProgressBar.js";
import { SearchInput } from "../components/ui/SearchInput.js";
import { SkeletonCardGrid } from "../components/ui/SkeletonCard.js";
import { useAuth } from "../context/AuthContext.js";
import { ApiError, fetchChecklists, type ApiChecklistSummary } from "../lib/api.js";
import { isChecklistArchived, partitionChecklists } from "../lib/checklistArchive.js";

function ChecklistCard({ chk }: { chk: ApiChecklistSummary }) {
  const { checked, total } = chk;
  const complete = total > 0 && checked >= total;
  return (
    <li>
      <CardLink to={`/checklists/${chk.id}`}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="text-base font-medium text-ink">{chk.name}</h2>
          <KindChip kind={chk.kind} />
        </div>
        <p className="mt-3 text-sm font-semibold tabular-nums text-ink">
          {checked}/{total}
          <span className="ml-1.5 font-normal text-muted-sm">
            {complete ? "complete" : "checked"}
            {chk.memberCount >= 2 ? ` · ${chk.memberCount} members` : ""}
            {isChecklistArchived(chk.archivedAt) ? " · Archived" : ""}
          </span>
        </p>
        <ProgressBar checked={checked} total={total} className="mt-2" />
      </CardLink>
    </li>
  );
}

export function MyChecklistsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const mySub = user?.sub ?? "";
  const [checklists, setChecklists] = useState<ApiChecklistSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchChecklists();
        if (!cancelled) setChecklists(data);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) return;
        setError(err instanceof ApiError ? err.message : "Failed to load checklists");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { activeOwn, activeShared, archived, hasAnySharedActive, filterActive } = useMemo(
    () => partitionChecklists(checklists, mySub, filter),
    [checklists, mySub, filter]
  );
  const noMatches =
    activeOwn.length === 0 && activeShared.length === 0 && archived.length === 0;

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:py-10">
      <PageHeader
        title="My Checklists"
        subtitle="The real thing you check off. Each one is a snapshot — editing a list later won't change a checklist you already started."
        actions={
          <>
            <Button type="button" variant="secondary" onClick={() => navigate("/templates")}>
              From templates
            </Button>
            <Button
              type="button"
              icon={<Plus size={16} aria-hidden="true" />}
              onClick={() => setNewOpen(true)}
            >
              New checklist
            </Button>
          </>
        }
      />

      <ErrorBanner message={error} />

      {!loading && checklists.length > 0 ? (
        <div className="mt-4">
          <SearchInput
            id="checklistFilter"
            aria-label="Search checklists"
            placeholder="Filter by name…"
            value={filter}
            onChange={setFilter}
          />
        </div>
      ) : null}

      {loading ? (
        <SkeletonCardGrid />
      ) : checklists.length === 0 ? (
        <EmptyState
          icon={<ListChecks size={28} className="text-muted" aria-hidden="true" />}
          title="Start your first checklist"
          body="A checklist is the thing you actually check off. Make one now — you can turn it into a reusable template later."
          primary={
            <Button type="button" onClick={() => setNewOpen(true)}>
              New checklist
            </Button>
          }
          secondary={
            <Button type="button" variant="secondary" onClick={() => setAiOpen(true)}>
              Describe it and let AI build it
            </Button>
          }
        />
      ) : noMatches ? (
        <p className="mt-8 text-sm text-muted">No checklists match &ldquo;{filter.trim()}&rdquo;.</p>
      ) : (
        <div className="mt-6 space-y-8">
          {activeOwn.length > 0 || activeShared.length > 0 ? (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
                Owned by me
              </h2>
              {activeOwn.length === 0 ? (
                <p className="mt-3 text-sm text-muted">No owned checklists in this filter.</p>
              ) : (
                <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                  {activeOwn.map((chk) => (
                    <ChecklistCard key={chk.id} chk={chk} />
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {hasAnySharedActive ? (
            <section>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-muted">
                Shared with me
              </h2>
              {activeShared.length === 0 ? (
                <p className="mt-3 text-sm text-muted">
                  No shared checklists match &ldquo;{filter.trim()}&rdquo;.
                </p>
              ) : (
                <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                  {activeShared.map((chk) => (
                    <ChecklistCard key={chk.id} chk={chk} />
                  ))}
                </ul>
              )}
            </section>
          ) : null}

          {archived.length > 0 ? (
            <details
              className="group"
              open={
                filterActive &&
                archived.length > 0 &&
                activeOwn.length === 0 &&
                activeShared.length === 0
                  ? true
                  : undefined
              }
            >
              <summary className="cursor-pointer text-sm font-semibold uppercase tracking-wide text-muted">
                Archived ({archived.length})
              </summary>
              <ul className={`mt-3 ${CARD_GRID_CLS}`}>
                {archived.map((chk) => (
                  <ChecklistCard key={chk.id} chk={chk} />
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      )}

      <NewChecklistModal open={newOpen} onClose={() => setNewOpen(false)} />
      <CreateWithAiModal open={aiOpen} onClose={() => setAiOpen(false)} />
    </div>
  );
}
